import { ActivityCode } from 'pleinchamp-api-client';
import React, { ComponentPropsWithRef, FC, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getApiEnumLabel } from '@api/business/api.utils';
import { PrimaryButton } from '@components/buttons/FlatButton/FlatButton.component';
import { Text } from '@components/Text/Text.component';
import { formValidator, ValueWithSelection } from '@form/business/form.types';
import { FormValidators, useFormState } from '@form/business/useFormState.hook';
import { Checkbox } from '@form/components/Checkbox/Checkbox.component';
import { Input } from '@form/components/Input/Input.component';
import { PlcForm } from '@form/components/PlcForm/PlcForm.component';
import { useTranslation } from '@locales/useTranslation.hook';
import { PlcDispatch } from '@store/store';
import { useClassNames } from '@styles/breakpoints';
import { userSelectors } from '@user/business/User.store';
import { usePlcRouter } from '@utils/customHooks';
import { ZIPCODE_MAX_LENGTH } from 'features/profile/business/formProfile.utils';

export interface MissingUserInformationProps extends ComponentPropsWithRef<'form'> {
  close: () => void;
  zipPostcode?: string;
  activities?: ActivityCode[];
}

type MissingUserInformationFormState = {
  zipCode: string;
  activitiesSelected: boolean;
};

const initialFormState: MissingUserInformationFormState = {
  activitiesSelected: false,
  zipCode: '',
};

const MissingUserInformation: FC<MissingUserInformationProps> = ({
  className,
  close,
  zipPostcode,
  activities,
  ...rest
}) => {
  const { t } = useTranslation(['search', 'common']);
  const user = useSelector(userSelectors.getUser);
  const router = usePlcRouter();
  const zipCodeInputRef = useRef<HTMLInputElement>(null);
  const activitiesRef = useRef<HTMLDivElement>(null);
  const {
    user: { putUser },
    settings: { putPreferences },
  } = useDispatch<PlcDispatch>();
  const showActivities = activities === undefined || activities?.length === 0;
  const showZipPostcode = zipPostcode === undefined || zipPostcode === '';

  const [activityValues, setActivityValues] = useState<ValueWithSelection[]>(
    Object.values(ActivityCode)
      .map(c => {
        return {
          label: getApiEnumLabel(c, t),
          selected: false,
          value: c.toString(),
        };
      })
      .concat({
        label: 'Aucune',
        selected: false,
        value: 'none',
      })
  );

  const fieldClassName = useClassNames({ all: ['plc-mb-m'], m: ['plc-mb-basis'] });

  function validateActivities() {
    return activityValues.some(v => v.selected);
  }

  // Validateur du formulaire applique les contrôles sur les saisies
  const validators: FormValidators<MissingUserInformationFormState> = {
    activitiesSelected: {
      method: () => {
        if (showActivities && !validateActivities()) {
          return [{ isValid: false, message: t('home.custom.popin-activities.required') }];
        }
        return [{ isValid: true, message: '' }];
      },
      ref: activitiesRef,
    },
    zipCode: {
      method: value => [formValidator(value, t, showZipPostcode).zipcode()],
      ref: zipCodeInputRef,
    },
  };

  // Les données de l'état du form (son state)
  const { formData, setFormField, isSubmitted, plcFormProps, disabledSubmitButton } = useFormState({
    initialFormState,
    validators,
  });

  const { zipCode } = formData;

  const onSelection = (value: ValueWithSelection, isChecked: boolean) => {
    setActivityValues(
      activityValues.map(v => {
        if (
          isChecked &&
          ((value.value === 'none' && v.value !== 'none') || (value.value !== 'none' && v.value === 'none'))
        ) {
          return { ...v, selected: false };
        }

        return v.value === value.value ? { ...v, selected: isChecked } : v;
      })
    );
  };

  async function onSubmitCallback() {
    if (showActivities) {
      const newPrefs = {
        activities: activityValues.filter(v => v.selected).map(v => v.value) as ActivityCode[],
        isPrefsSet: true,
      };
      await putPreferences(newPrefs);
    }

    if (showZipPostcode && user && user.address) {
      await putUser({ ...user, address: { ...user.address, zipPostcode: zipCode } });
    }

    return router.reload();
  }

  return (
    <>
      <div className="plc-mb-basis">
        <Text i18nKey="home.custom.popin-activities.description" variant="p" />
      </div>
      <PlcForm autoComplete="off" onSubmitCallback={onSubmitCallback} {...plcFormProps} {...rest}>
        {showZipPostcode && (
          <fieldset>
            <Input
              ref={zipCodeInputRef}
              className={fieldClassName}
              isHintVisible={isSubmitted}
              labelI18n="profile.profile-tab.form-address.zip-code.label"
              labelVariant="bold"
              maxLength={ZIPCODE_MAX_LENGTH}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFormField('zipCode', event.target.value)}
              required={true}
              validators={validators.zipCode?.method(zipCode)}
              value={zipCode}
              variant="small-input"
            />
          </fieldset>
        )}
        {showActivities && (
          <fieldset className="plc-mb-s">
            <Text
              className="plc-mb-m"
              i18nKey="home.custom.popin-activities.title"
              style={{ fontWeight: 'bold' }}
              variant="p"
            />
            <div ref={activitiesRef} className="grid-m-2-s-1" style={{ margin: 0 }}>
              {activityValues.map(v => (
                <Checkbox
                  key={v.value}
                  checked={v.selected}
                  label={v.label}
                  multilineLabel
                  onCheck={checked => onSelection(v, checked)}
                />
              ))}
            </div>
            {isSubmitted && !validateActivities() && (
              <Text
                key="home.custom.popin-activities.required"
                className="visible error"
                flavour="red"
                tag="p"
                variant="tiny"
              >
                {t('home.custom.popin-activities.required')}
              </Text>
            )}
          </fieldset>
        )}
        <Text
          className="plc-mb-s"
          flavour="grey"
          i18nKey={showActivities && showZipPostcode ? 'common.required-fields' : 'common.required-field'}
          tag="p"
          variant="tiny"
        />
        <div className="flex align-center justify-center">
          <PrimaryButton disabled={disabledSubmitButton} textProps={{ i18nKey: 'common.save' }} type="submit" />
        </div>
      </PlcForm>
    </>
  );
};

export { MissingUserInformation };
